import { Vector3 } from 'three';
import type { Room } from '../world/Room';
import type { Surface } from '../world/Surface';
import type { CollisionWorld } from '../world/CollisionWorld';
import { projectOnPlane } from '../utils/math';

const scratch = new Vector3();

/** Thrust applied while free-floating, along the camera basis. */
export function applyThrust(
  velocity: Vector3,
  move: { x: number; y: number },
  viewForward: Vector3,
  viewRight: Vector3,
  acceleration: number,
  dt: number,
): void {
  if (move.x === 0 && move.y === 0) return;
  scratch.copy(viewForward).multiplyScalar(move.y).addScaledVector(viewRight, move.x);
  if (scratch.lengthSq() < 1e-8) return;
  scratch.normalize().multiplyScalar(acceleration * dt);
  velocity.add(scratch);
}

/** Exponential damping so a free-floating body eventually coasts to rest. */
export function applyDamping(velocity: Vector3, damping: number, dt: number): void {
  const factor = Math.exp(-damping * dt);
  velocity.multiplyScalar(factor);
}

/**
 * Find the surface the body is about to touch.
 *
 * The contact is measured with the body centre rather than the feet: a robot
 * can reach a surface in any orientation (head first towards a ceiling, for
 * example) and the magnetic feet reorient onto the surface when it lands.
 */
export function findLandingCandidate(
  room: Room,
  bodyCenter: Vector3,
  velocity: Vector3,
  threshold: number,
): Surface | null {
  let best: Surface | null = null;
  let bestDistance = Infinity;
  const zoneId = room.zoneAt(bodyCenter);

  for (const surface of room.attachableSurfaces) {
    // Only the zone that contains the body can catch it. Another zone's wall
    // sits behind this one at a shared boundary and would otherwise snap the
    // body through the wall.
    if (zoneId && surface.zoneId !== zoneId) continue;
    const distance = surface.signedDistance(bodyCenter);
    if (distance > threshold || distance < -threshold) continue;
    // Only attach when moving towards the surface (or already resting on it).
    if (velocity.dot(surface.normal) > 0.5) continue;

    const local = surface.toLocal(bodyCenter);
    // A doorway is an opening with no material: nothing to land on.
    if (!surface.isSolidAt(local.u, local.v, 0.05)) continue;

    if (Math.abs(distance) < bestDistance) {
      bestDistance = Math.abs(distance);
      best = surface;
    }
  }

  return best;
}

/** Find a magnetic surface close enough to grab while free-floating. */
export function findGrabCandidate(room: Room, bodyCenter: Vector3, radius: number): Surface | null {
  let best: Surface | null = null;
  let bestDistance = Infinity;
  const zoneId = room.zoneAt(bodyCenter);

  for (const surface of room.attachableSurfaces) {
    if (zoneId && surface.zoneId !== zoneId) continue;
    const distance = surface.signedDistance(bodyCenter);
    if (distance < 0 || distance > radius) continue;

    const local = surface.toLocal(bodyCenter);
    // Nothing to grab on an opening either.
    if (!surface.isSolidAt(local.u, local.v, 0.05)) continue;

    if (distance < bestDistance) {
      bestDistance = distance;
      best = surface;
    }
  }

  return best;
}

/** Velocity component that lies in the plane of a surface. */
export function tangentialVelocity(
  velocity: Vector3,
  surface: Surface,
  out = new Vector3(),
): Vector3 {
  return projectOnPlane(velocity, surface.normal, out);
}

export interface LandingPrediction {
  /** The surface the body is heading for, or null when there is none. */
  surface: Surface | null;
  /** The distance to that surface, metres. */
  distance: number;
}

const scratchDirection = new Vector3();

/**
 * Predict the surface the body will land on.
 *
 * A ray along the velocity finds the surface the body is heading for, and the
 * distance to it gives the time to contact - which is how long the body has to
 * turn itself around before it lands.
 *
 * An obstacle or a miss returns no surface: the caller keeps its previous
 * prediction rather than spinning on a bad guess.
 */
export function predictLanding(
  room: Room,
  collision: CollisionWorld,
  position: Vector3,
  velocity: Vector3,
  maxDistance: number,
): LandingPrediction {
  const speed = velocity.length();
  if (speed < 0.05) return { surface: null, distance: 0 };

  scratchDirection.copy(velocity).divideScalar(speed);
  const hit = collision.raycast(position, scratchDirection, maxDistance);
  if (!hit || hit.kind !== 'surface' || !hit.surface) return { surface: null, distance: 0 };
  if (!hit.surface.magnetic) return { surface: null, distance: 0 };
  // The room is only used to keep the surface set authoritative.
  void room;
  return { surface: hit.surface, distance: hit.distance };
}
